import type { Node } from '@xyflow/react'
import { readUi, writeUi } from '../uiPrefs'

/**
 * Frames on the canvas
 * -------------------------------------------------------------
 * A frame holds pictures the way a Miro frame does (Huanrui, 2026-09-30):
 * what is inside moves with it, it is resized freely and starts at 16:9, and
 * its ports are for skills that take a set of pictures in no particular
 * order. No frame inside a frame, ever.
 *
 * WHY MEMBERSHIP IS A FIELD AND NOT REACT FLOW'S PARENT
 *
 * React Flow has parent nodes, and a child of one stores its position relative
 * to the parent. Every piece of this board that does arithmetic on positions
 * -- tidy, triple-click, paste, Alt-drag, dropping on the chat, placing a run
 * -- reads `node.position` as a place on the board, and each would have been
 * quietly wrong for a picture in a frame. The measured lesson from Miro
 * (docs: miro-parenting-and-coordinates) was exactly this: a coordinate whose
 * meaning depends on how the item got there is a coordinate that will be
 * misread.
 *
 * So a picture in a frame is an ordinary picture with `frame: <frame id>` in
 * its data, positioned on the board like everything else. Moving the frame
 * moves its members by the same amount (ProjectCanvas, onNodesChange).
 * Membership is decided by where a picture's centre is, when it is let go.
 */

/** The shape a frame starts at. */
export const FRAME_RATIO = 16 / 9
/** Space between a frame's edge and what is in it. */
export const FRAME_PAD = 48
/** Space kept between a new frame and anything already on the board. */
export const FRAME_CLEARANCE = 80
/** An empty frame, made from the right-click menu. */
export const EMPTY_FRAME = { w: 800, h: 450 }
/**
 * Beneath everything. React Flow lifts a selected node by 1000, so a frame at
 * -2000 stays under its pictures even while it is the one selected.
 */
export const FRAME_Z = -2000

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export const isFrame = (node: Node): boolean => node.type === 'frame'

/** The frame a picture belongs to, if any. */
export function frameOf(node: Node): string {
  const id = (node.data as { frame?: unknown }).frame
  return typeof id === 'string' ? id : ''
}

const pageOf = (node: Node): string => (node as { page?: string }).page ?? ''

/** What a node takes up on the board, measured if it has been. */
export function boxOf(node: Node): Rect {
  return {
    x: node.position.x,
    y: node.position.y,
    w: node.measured?.width ?? node.width ?? 220,
    h: node.measured?.height ?? node.height ?? 160,
  }
}

function contains(outer: Rect, x: number, y: number): boolean {
  return x >= outer.x && x <= outer.x + outer.w && y >= outer.y && y <= outer.y + outer.h
}

function overlaps(a: Rect, b: Rect, gap: number): boolean {
  return (
    a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y
  )
}

/** The smallest rectangle around all of them. */
export function bounds(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null
  const left = Math.min(...rects.map((r) => r.x))
  const top = Math.min(...rects.map((r) => r.y))
  const right = Math.max(...rects.map((r) => r.x + r.w))
  const bottom = Math.max(...rects.map((r) => r.y + r.h))
  return { x: left, y: top, w: right - left, h: bottom - top }
}

/**
 * A frame around some content: padded, then widened or heightened to 16:9,
 * with the content in the middle.
 */
/**
 * Things in reading order (Huanrui, 2026-10-23): rows from the top -- one
 * that starts above the middle of a row's first is in that row -- each row
 * from the left. How several frames are sent to Miro and tidied.
 */
export function readingOrder(list: Node[]): Node[] {
  const lines: Node[][] = []
  for (const node of [...list].sort((a, b) => boxOf(a).y - boxOf(b).y)) {
    const line = lines[lines.length - 1]
    const first = line ? boxOf(line[0]) : null
    if (line && first && boxOf(node).y < first.y + first.h / 2) line.push(node)
    else lines.push([node])
  }
  return lines.flatMap((line) => line.sort((a, b) => boxOf(a).x - boxOf(b).x))
}

export function frameAround(content: Rect): Rect {
  let w = content.w + FRAME_PAD * 2
  let h = content.h + FRAME_PAD * 2
  if (w / h < FRAME_RATIO) w = h * FRAME_RATIO
  else h = w / FRAME_RATIO
  return {
    x: content.x - (w - content.w) / 2,
    y: content.y - (h - content.h) / 2,
    w: Math.round(w),
    h: Math.round(h),
  }
}

/**
 * Work out which frame each of `ids` is in, and write it down.
 *
 * By the centre of the picture, and the smallest frame containing it when
 * frames overlap -- the one that looks like it is holding it. Only pictures:
 * a skill card sitting over a frame is not in it. Only frames on the same
 * page. Answers the same array when nothing changed, so React has nothing to
 * redraw.
 */
export function settle(nodes: Node[], ids: Set<string>): Node[] {
  const frames = nodes.filter(isFrame)
  let changed = false
  const next = nodes.map((node) => {
    if (!ids.has(node.id) || node.type !== 'image') return node
    const box = boxOf(node)
    const cx = box.x + box.w / 2
    const cy = box.y + box.h / 2
    let best: Node | null = null
    let bestArea = Infinity
    for (const frame of frames) {
      if (pageOf(frame) !== pageOf(node)) continue
      const rect = boxOf(frame)
      if (!contains(rect, cx, cy)) continue
      const area = rect.w * rect.h
      if (area < bestArea) {
        best = frame
        bestArea = area
      }
    }
    const want = best?.id ?? ''
    if (want === frameOf(node)) return node
    changed = true
    const data = { ...(node.data as Record<string, unknown>) }
    if (want) data.frame = want
    else delete data.frame
    return { ...node, data }
  })
  return changed ? next : nodes
}

/**
 * Pictures of these sizes in rows of one height, the row length chosen so the
 * whole block comes out closest to 16:9. Positions are from the block's top
 * left.
 */
export function rows(
  sizes: { w: number; h: number }[],
  height: number,
  gap: number,
): { places: { x: number; y: number; w: number; h: number }[]; w: number; h: number } {
  const widths = sizes.map((size) => Math.round(size.h > 0 ? (size.w * height) / size.h : height))
  let best: ReturnType<typeof lay> | null = null
  for (let perRow = 1; perRow <= Math.max(1, sizes.length); perRow += 1) {
    const laid = lay(widths, height, gap, perRow)
    if (
      !best ||
      Math.abs(Math.log(laid.w / laid.h / FRAME_RATIO)) < Math.abs(Math.log(best.w / best.h / FRAME_RATIO))
    ) {
      best = laid
    }
  }
  return best ?? { places: [], w: 0, h: 0 }
}

function lay(widths: number[], height: number, gap: number, perRow: number) {
  const places: { x: number; y: number; w: number; h: number }[] = []
  let y = 0
  let widest = 0
  for (let start = 0; start < widths.length; start += perRow) {
    let x = 0
    for (const w of widths.slice(start, start + perRow)) {
      places.push({ x, y, w, h: height })
      x += w + gap
    }
    widest = Math.max(widest, x - gap)
    y += height + gap
  }
  return { places, w: Math.max(1, widest), h: Math.max(1, y - gap) }
}

/**
 * Somewhere a rectangle of this size can go without covering anything.
 *
 * In the part of the board being looked at if there is room, as near its
 * middle -- or the pointer, when given -- as possible; otherwise to the right
 * of everything on the page. What
 * is "anything" is the caller's: the nodes on the page being looked at.
 */
export function clearSpot(
  size: { w: number; h: number },
  taken: Rect[],
  view: Rect,
  /** Where to start looking, if not the middle of the screen: the pointer. */
  near?: { x: number; y: number },
): { x: number; y: number } {
  const from = near ?? { x: view.x + view.w / 2, y: view.y + view.h / 2 }
  const centre = { x: from.x - size.w / 2, y: from.y - size.h / 2 }
  const free = (x: number, y: number) =>
    !taken.some((rect) => overlaps({ x, y, w: size.w, h: size.h }, rect, FRAME_CLEARANCE))

  if (free(centre.x, centre.y)) return centre

  // Outwards from the middle in rings, staying inside what is on screen.
  const step = Math.max(60, Math.round(Math.min(size.w, size.h) / 4))
  const reach = Math.max(view.w, view.h)
  for (let ring = 1; ring * step <= reach; ring += 1) {
    for (let i = -ring; i <= ring; i += 1) {
      for (const [dx, dy] of [
        [i, -ring],
        [i, ring],
        [-ring, i],
        [ring, i],
      ]) {
        const x = centre.x + dx * step
        const y = centre.y + dy * step
        const inside =
          x >= view.x && y >= view.y && x + size.w <= view.x + view.w && y + size.h <= view.y + view.h
        if (inside && free(x, y)) return { x, y }
      }
    }
  }

  // No room on screen: beside everything, top-aligned with it.
  const all = bounds(taken)
  if (!all) return centre
  return { x: all.x + all.w + FRAME_CLEARANCE * 2, y: all.y }
}

/* ---------- a frame's shape and colour (the bar over a selected frame) ---------- */

/** The shapes offered. A new frame is always 16:9, whatever was chosen last. */
export const FRAME_RATIOS: { label: string; value: number }[] = [
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '1:1', value: 1 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
]

/**
 * The colours offered. Grey first, and the default: white on a dark board was
 * too bright (Huanrui, 2026-09-30). The rest stay quiet, because a frame is
 * a surface for pictures to sit on, not a picture itself.
 */
export const FRAME_COLORS: { name: string; value: string }[] = [
  { name: 'Grey', value: '#d9d9d9' },
  { name: 'White', value: '#ffffff' },
  { name: 'Stone', value: '#b8b8b8' },
  { name: 'Charcoal', value: '#2e2e2e' },
  { name: 'Sand', value: '#e6dccb' },
  { name: 'Sage', value: '#d3ddd2' },
  { name: 'Mist', value: '#d2dce6' },
  { name: 'Blush', value: '#e8d6d6' },
]
export const DEFAULT_FRAME_COLOR = FRAME_COLORS[0].value

const COLOR_KEY = 'ww.canvas.frame-color'

/**
 * The colour a new frame starts in: the last one this person picked, in this
 * browser, or grey. Kept with the board's other looks (paper, grid), which
 * are remembered the same way.
 */
export function readFrameColor(): string {
  return readUi(COLOR_KEY, DEFAULT_FRAME_COLOR, (saved) => FRAME_COLORS.some((one) => one.value === saved))
}

/** Kept with the account as well (uiPrefs.ts, 2026-10-02). */
export function writeFrameColor(value: string): void {
  writeUi(COLOR_KEY, value)
}

/** Whether a colour is dark enough to want a light edge rather than a dark one. */
export function isDarkColor(hex: string): boolean {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!match) return false
  const n = parseInt(match[1], 16)
  const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
  return lum < 0.45
}

/**
 * The same frame at another shape: the top-left corner stays put and the area
 * stays about the same, so a landscape frame turned portrait does not shoot
 * off the bottom of the screen.
 */
export function reshape(rect: Rect, ratio: number): Rect {
  const area = Math.max(1, rect.w * rect.h)
  return {
    x: rect.x,
    y: rect.y,
    w: Math.round(Math.sqrt(area * ratio)),
    h: Math.round(Math.sqrt(area / ratio)),
  }
}

/**
 * A block of pictures laid out by `rows`, made to fit a frame of this size:
 * scaled down (never up) to fit inside the padding, and centred. Positions
 * are from the frame's top left.
 */
export function fitInFrame(
  block: { places: { x: number; y: number; w: number; h: number }[]; w: number; h: number },
  frame: { w: number; h: number },
): { x: number; y: number; w: number; h: number }[] {
  const room = { w: Math.max(1, frame.w - FRAME_PAD * 2), h: Math.max(1, frame.h - FRAME_PAD * 2) }
  const k = Math.min(1, room.w / Math.max(1, block.w), room.h / Math.max(1, block.h))
  const left = (frame.w - block.w * k) / 2
  const top = (frame.h - block.h * k) / 2
  return block.places.map((place) => ({
    x: Math.round(left + place.x * k),
    y: Math.round(top + place.y * k),
    w: Math.max(1, Math.round(place.w * k)),
    h: Math.max(1, Math.round(place.h * k)),
  }))
}
